// DrawComponent.js
import * as turf from '@turf/turf';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { v4 as uuidv4 } from 'uuid';
import { useEffect } from 'react';

async function initializeMapboxDraw(globalRefs, globalState) {

  let mapRef = globalRefs.mapRef;
  let longstandingGeoJSONRef = globalRefs.longstandingGeoJSONRef;
  let drawRef = globalRefs.drawRef;
  let setIsLoading = globalState.setIsLoading;
  let selectedYearEditing = globalState.selectedYearEditing;
  let setIsLanduse = globalState.setIsLanduse;
  let setHasCreatedNewPolygonInModify = globalState.setHasCreatedNewPolygonInModify;
  let selectedMetric = globalState.selectedMetric;
  let isDrawing = globalState.isDrawing;
  let setFinishedEditing = globalState.setFinishedEditing;
  let polygonCustomLayerRef = globalRefs.polygonCustomLayerRef;
  let selectedKey = globalState.selectedKey;
  let setStoreEditedBoundaryFeatureSkylarkID = globalState.setStoreEditedBoundaryFeatureSkylarkID;
  let activeLayer = globalState.activeLayer;

  let updating = 0;
  let creating = 0;
  let deleting = 0;

  let yearsAvailable = globalState.yearsAvailable;

  let setRecentChangeList = globalState.setRecentChangeList;
  let recentChangeList = globalState.recentChangeList;

  if (isDrawing) {
    if (drawRef.current) {
      // mapRef.current.removeControl(drawRef.current);
      // drawRef.current = null;
    }
  }

  // Remove previous listeners if they exist
  if (drawRef.current && drawRef.current.listeners) {
    mapRef.current.off('draw.update', drawRef.current.listeners.updateHandler);
    mapRef.current.off('draw.create', drawRef.current.listeners.createHandler);
    mapRef.current.off('draw.delete', drawRef.current.listeners.deleteHandler);
  }

  // Initialize MapboxDraw only if it's not already initialized

  if (!mapRef.current._controls?.includes(drawRef.current)) {
    mapRef.current.addControl(drawRef.current);
  }

  const geojson = longstandingGeoJSONRef.current;

  const updatedFeatures = geojson.features.map(feature => {
    return {
      ...feature,
      properties: {
        ...feature.properties,
        skylark_id: feature.skylark_id,
        id: feature.skylark_id, // Mapbox Draw expects an 'id' property
        feature_data_standard: feature.feature_data_standard,
        feature_data_simple_timeseries: feature.feature_data_simple_timeseries,
        feature_data_simple: feature.feature_data_simple,
        external_ids: feature.external_ids,
        successor_skylark_id: feature.successor_skylark_id,
      }
    };
  });

  const filteredFeatures = updatedFeatures.filter(feature => {
    const { feature_data_standard, feature_data_simple_timeseries, geometry } = feature;
    const selectedDate = new Date(selectedYearEditing);
    const deletedDate = feature_data_standard.deleted ? new Date(feature_data_standard.deleted) : null;
    const createdDate = feature_data_standard.created ? new Date(feature_data_standard.created) : null;

    // 1️⃣ Exclude deleted features (deleted on or before selected date)
    if (deletedDate && deletedDate <= selectedDate) {
      console.log("deleted");
      return false;
    }

    // 2️⃣ Exclude features created *after* selected date
    if (createdDate && createdDate > selectedDate) {
      console.log("created after selected year");
      return false;
    }

    // 3️⃣ Exclude if layer doesn’t match
    if (feature_data_standard.layer !== activeLayer) {
      return false;
    }

    // 4️⃣ Handle boundary change
    if (feature_data_simple_timeseries?.BoundaryChange?.[selectedYearEditing] !== "000,000") {
      console.log("boundary changed");
      try {
        geometry.coordinates = JSON.parse(feature_data_simple_timeseries.BoundaryChange[selectedYearEditing]);
      } catch (error) {
        console.error("Failed to parse BoundaryChange coordinates:", error);
        // optionally: return false;
      }
    }

    return true;
  });

  // Create a new GeoJSON object with the filtered features
  const updatedGeojson = {
    type: "FeatureCollection",
    features: filteredFeatures
  };

  console.log(geojson);

  // Ensure the GeoJSON is valid before adding
  // if (drawRef.current && updatedGeojson.features.length > 0) {
  drawRef.current.add(updatedGeojson);

  // Define new listeners
  const updateListener = drawUpdateHandler(
    mapRef,
    longstandingGeoJSONRef,
    selectedYearEditing,
    updating,
    setFinishedEditing,
    polygonCustomLayerRef,
    selectedKey,
    setStoreEditedBoundaryFeatureSkylarkID,
    setRecentChangeList,
    recentChangeList,
    yearsAvailable
  );
  const createListener = drawCreateHandler(
    mapRef,
    longstandingGeoJSONRef,
    selectedYearEditing,
    selectedMetric,
    creating,
    setHasCreatedNewPolygonInModify,
    setIsLanduse,
    setFinishedEditing,
    setRecentChangeList,
    recentChangeList
  );
  const deleteListener = drawDeleteHandler(
    mapRef,
    longstandingGeoJSONRef,
    selectedYearEditing,
    deleting,
    setFinishedEditing,
    setRecentChangeList,
    recentChangeList
  );

  // Store references to the new listeners in the drawRef for later removal

  drawRef.current.listeners = {
    updateHandler: updateListener,
    createHandler: createListener,
    deleteHandler: deleteListener
  };

  // Attach the new listeners to the map

  mapRef.current.on('draw.update', updateListener);
  mapRef.current.on('draw.create', createListener);
  mapRef.current.on('draw.delete', deleteListener);

  // setIsLoading(false);
}

export const drawUpdateHandler = (
  mapRef,
  longstandingGeoJSONRef,
  selectedYearEditing,
  updating,
  setFinishedEditing,
  polygonCustomLayerRef,
  selectedKey,
  setStoreEditedBoundaryFeatureSkylarkID,
  setRecentChangeList,
  recentChangeList,
  yearsAvailable
) => (e) => {
  setFinishedEditing(false);

  let years = yearsAvailable;

  console.log(years)

  const updatedFeatures = e.features;
  const changedFeatures = [];

  if (updatedFeatures.length > 0) {
    const geojsonCopy = structuredClone(longstandingGeoJSONRef.current); // Work on a deep copy

    updatedFeatures.forEach((updatedFeature) => {
      const skylarkId = updatedFeature.properties.skylark_id;

      geojsonCopy.features.forEach((feature) => {
        if (feature.skylark_id === skylarkId) {
          const index = years.indexOf(selectedYearEditing);
          console.log("selectedYearEditing: ", selectedYearEditing, "years index:", years);
          if (index !== -1) {
            for (let j = index; j < years.length; j++) {
              const year = years[j];

              console.log("Updating year:", year);

              // Append new coordinates
              feature.feature_data_simple_timeseries.BoundaryChange = {
                ...feature.feature_data_simple_timeseries.BoundaryChange,
                [year]: JSON.stringify(updatedFeature.geometry.coordinates)
              };

              // Update area
              feature.feature_data_simple_timeseries.Shape_Area = {
                ...feature.feature_data_simple_timeseries.Shape_Area,
                [year]: turf.area({
                  type: "Feature",
                  geometry: updatedFeature.geometry,
                  properties: {}
                })
              };
            }
          }

          changedFeatures.push({ toc: 'BoundaryChange', newFeatureChange: structuredClone(feature) });
          setStoreEditedBoundaryFeatureSkylarkID(feature.skylark_id);
        }
      });
    });

    // Commit changes back to the ref
    longstandingGeoJSONRef.current = geojsonCopy;

    if (changedFeatures.length > 0) {
      setRecentChangeList(prev => {
        const newHistory = [...prev, changedFeatures];
        return newHistory.slice(-20);
      });
    }
  }

  setFinishedEditing(true);
};

// Draw create event

export const drawCreateHandler = (
  mapRef,
  longstandingGeoJSONRef,
  selectedYearEditing,
  selectedMetric,
  creating,
  setHasCreatedNewPolygonInModify,
  setIsLanduse,
  setFinishedEditing,
  setRecentChangeList
) => (e) => {
  if (creating === 0) {
    const originalFeature = longstandingGeoJSONRef.current.features[1]; // Assuming feature 1 is the template

    // Deep clone to ensure no references to originalFeature remain
    const newFeature = JSON.parse(JSON.stringify({
      external_ids: {
        "searchlight_property_id": uuidv4(),
        "OBJECTID": uuidv4(),
        "OID_": uuidv4(),
        "id": uuidv4(),
      },
      feature_data_simple: originalFeature.feature_data_simple,
      feature_data_simple_timeseries: originalFeature.feature_data_simple_timeseries,
      feature_data_standard: {
        ...originalFeature.feature_data_standard,
        created: selectedYearEditing, // Set created to current selectedYearEditing
      },
      feature_data_standard_calculated: {
        "Shape_Area": turf.area(e.features[0].geometry),
        "Shape_Length": turf.length(e.features[0].geometry),
      },
      geometry: e.features[0].geometry, // Use the newly drawn geometry
      skylark_id: uuidv4(),
      successor_skylark_id: uuidv4(),
    }));

    // Create a new GeoJSON FeatureCollection
    const updatedGeoJSON = {
      type: "FeatureCollection",
      features: [...longstandingGeoJSONRef.current.features, newFeature], // Copy existing features and add the new one
    };

    // Update longstandingGeoJSONRef with the new GeoJSON
    longstandingGeoJSONRef.current = updatedGeoJSON;

    const historyEntry = [{
      toc: 'BoundaryCreated',
      newFeatureChange: structuredClone(newFeature),
    }];
    setRecentChangeList(prev => {
      const newHistory = [...prev, historyEntry];
      return newHistory.slice(-20);
    });

    creating = 1;
  }
};

export const drawDeleteHandler = (
  mapRef,
  longstandingGeoJSONRef,
  selectedYearEditing,
  deleting,
  setFinishedEditing,
  setRecentChangeList
) => (e) => {
  setFinishedEditing(false);

  if (deleting === 0) {
    console.log("deleted");
    const updatedFeatures = e.features;
    console.log("updatedFeatures to delete:", updatedFeatures);

    if (updatedFeatures.length > 0) {
      const changedFeatures = [];

      // Create a new feature array with the modifications
      const newFeatures = longstandingGeoJSONRef.current.features.map((feature) => {
        const matchedUpdate = updatedFeatures.find(
          (updatedFeature) => updatedFeature.properties.skylark_id === feature.skylark_id
        );

        if (matchedUpdate) {
          // Create the updated feature with `deleted`
          const updatedFeature = {
            ...feature,
            feature_data_standard: {
              ...feature.feature_data_standard,
              deleted: selectedYearEditing,
            },
          };

          console.log("Feature following deletion:", updatedFeature);

          // ✅ Push the updated version (so history reflects the "deleted" flag)
          changedFeatures.push({
            toc: "BoundaryDeleted",
            newFeatureChange: structuredClone(updatedFeature),
          });

          return updatedFeature;
        }

        return feature;
      });

      // Assign the updated FeatureCollection back to longstandingGeoJSONRef
      longstandingGeoJSONRef.current = {
        ...longstandingGeoJSONRef.current,
        features: newFeatures,
      };

      if (changedFeatures.length > 0) {
        setRecentChangeList((prev) => {
          const newHistory = [...prev, changedFeatures];
          return newHistory.slice(-20); // cap at 20
        });
      }
    }

    // ⚠ If deleting is supposed to persist, consider a setter instead
    deleting = 1;
  }

  setFinishedEditing(true);
};


export default initializeMapboxDraw;
